import { AsyncPipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, map, switchMap, tap } from 'rxjs';

import { SiteState } from 'src/app/shared/models';

// eslint-disable-next-line max-len
import { SpecificFormHeaderSectionComponent } from '../../../components/questionnaire/form-header-section/specific-form-header-section/specific-form-header-section.component';
import { loadCropFields } from '../../../store/tga.actions';
import { getTgaCropYear, selectCropFieldById } from '../../../store/tga.selectors';
import { TgaBiomassEvaluationComponent } from '../tga-biomass-evaluation/tga-biomass-evaluation.component';
import { TgaFertilizerEvaluationComponent } from '../tga-fertilizer-evaluation/tga-fertilizer-evaluation.component';
import { TgaGeneralInformationEvaluationComponent } from '../tga-general-information-evaluation/tga-general-information-evaluation.component';
import { TgaPhytophthoraEvaluationComponent } from '../tga-phytophthora-evaluation/tga-phytophthora-evaluation.component';
import { TgaVeldwaarnemingenEvaluationComponent } from '../tga-veldwaarnemingen-evaluation/tga-veldwaarnemingen-evaluation.component';

export enum TgaEvaluationView {
  GeneralInformation = 'general-information',
  Phytophthora = 'phytophthora',
  Fertilizer = 'fertilizer',
  Veldwaarnemingen = 'veldwaarnemingen',
  Gewasgroei = 'gewasgroei',
}

@Component({
  selector: 'app-tga-evaluation-advice',
  templateUrl: './tga-evaluation-advice.component.html',
  styleUrl: './tga-evaluation-advice.component.scss',
  imports: [
    AsyncPipe,
    TranslatePipe,
    SpecificFormHeaderSectionComponent,
    TgaGeneralInformationEvaluationComponent,
    TgaPhytophthoraEvaluationComponent,
    TgaFertilizerEvaluationComponent,
    TgaVeldwaarnemingenEvaluationComponent,
    TgaBiomassEvaluationComponent,
  ],
})
export class TgaEvaluationAdviceComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject<Store<SiteState>>(Store);

  readonly evaluationView = TgaEvaluationView;
  readonly selectedView = signal(TgaEvaluationView.GeneralInformation);
  readonly isGlobalAdministrator$ = this.store.select(
    AuthorizationSelectors.hasGlobalAdministratorRoleForApplicationKey('TGACA'),
  );
  readonly context$ = combineLatest([this.route.params, this.store.select(getTgaCropYear)]).pipe(
    map(([params, cropYear]) => ({
      cropFieldId: Number(params['cropFieldId']),
      cropYear,
    })),
    distinctUntilChanged(
      (previous, current) => previous.cropFieldId === current.cropFieldId && previous.cropYear === current.cropYear,
    ),
    switchMap(({ cropFieldId, cropYear }) =>
      this.store.select(selectCropFieldById(cropFieldId)).pipe(
        tap((cropField) => {
          if (!cropField && cropFieldId) {
            this.store.dispatch(loadCropFields({ cropYear }));
          }
        }),
        map((cropField) => ({ cropField, cropYear })),
      ),
    ),
  );

  selectView(view: TgaEvaluationView): void {
    this.selectedView.set(view);
  }
}
